// @flow
import shallowEqualObjects from 'shallow-equal/objects';

/**
 * Checks if a variation matching the configuration exists in WooCommerce
 *
 * @deprecated 0.1.0 No need to get existing variations.
 *
 * @param   {object}  configData The user's configuration.
 * @param   {array}   variations Existing variations of WC.
 * @returns {boolean} True if found, false if not.
 */
export const checkIfVariationExists = ({
  configData,
  variations,
}: {
  configData: Object,
  variations: Array<Object>,
}) => {
  if (!variations.length) return false;
  // Create config object for comparison.
  const configKeys = Object.keys(configData);
  const config = {};
  configKeys.forEach(attribute => {
    // Limit to attribute keys.
    if (attribute.substr(0, 3) === 'pa_') {
      config[configData[attribute].title.toLowerCase()] = configData[
        attribute
      ].value.toLowerCase();
    }
  });
  for (let i = 0; i < variations.length; i += 1) {
    // Create variation object for comparison
    const variationConfig = {};
    variations[i].attributes.forEach(attribute => {
      variationConfig[
        attribute.name.toLowerCase()
      ] = attribute.option.toLowerCase();
    });
    if (shallowEqualObjects(config, variationConfig)) {
      // Variation exists already
      return variations[i].id;
    }
  }
  // No such variation
  return false;
};

/**
 * Extracts the first number from given slug.
 *
 * @param   {string} slug Wordpress slug
 * @returns {string}      Number as string or empty string.
 */
export const getNumberFromSlug = (slug: string = '') => {
  let number;
  slug.split('_').some(part => {
    if (Number.isFinite(+part)) {
      number = part;
      return true;
    }
    return false;
  });

  if (number) {
    return number;
  }

  return '';
};

/**
 * For when a change event needs to be fired manually.
 *
 * @returns {Object} Dummy change event.
 */
export const buildDummyChangeEvent = ({
  description,
  id,
  label,
  price,
  slug,
  time,
  title,
  value,
}: {
  description: string,
  label: string,
  price: string,
  slug: string,
  time: string,
  title: string,
  value: string,
}) => ({
  event: {
    nativeEvent: {
      target: {
        getAttribute(attr: string) {
          switch (attr) {
            case 'data-id':
              return id;
            case 'data-description':
              return description;
            case 'data-price':
              return price;
            case 'data-time':
              return time;
            default:
              return '';
          }
        },
        textContent: label,
      },
    },
    target: { value },
  },
  slug,
  title,
});

/**
 * Smoothly scrolls element into view if it's below the current view of the container.
 */
export const smoothScrollIntoView = (element: Object, container: Object) => {
  /* eslint-disable no-param-reassign */
  if (!(element instanceof Element) || !(container instanceof Element)) {
    return;
  }

  let scrollIncr = container.scrollTop;
  const scrollEnd =
    element.offsetTop - element.offsetHeight * 2.8 - container.offsetHeight;

  if (scrollIncr > scrollEnd) {
    return;
  }

  const scrollInterval = setInterval(() => {
    scrollIncr += 5;
    if (scrollIncr < scrollEnd) {
      container.scrollTop = scrollIncr;
    } else {
      container.scrollTop = scrollEnd;
      clearInterval(scrollInterval);
    }
  }, 10);
  /* eslint-enable no-param-reassign */
};

/**
 * Comparison function for object with the property `name`.
 *
 * Compares first as numbers, then falls back to strings.
 *
 * @returns {number} -1, 0, 1
 */
export const orderByName = (a: Object, b: Object) => {
  const aName = a.name;
  const bName = b.name;
  const aNum = +aName;
  const bNum = +bName;

  if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
    return aNum - bNum;
  }

  switch (aName > bName) {
    case true:
      return 1;
    case false:
      return -1;
    default:
      return 0;
  }
};

export const getQueryVariable = (variable: string) => {
  const query = window.location.search.substring(1);
  const vars = query.split('&');
  const results = [];
  for (let i = 0; i < vars.length; i += 1) {
    const pair = vars[i].split('=');
    if (decodeURIComponent(pair[0]) === variable) {
      results.push(decodeURIComponent(pair[1]));
    }
  }
  return results.join(',');
};



// WEBPACK FOOTER //
// ./src/assets/scripts/helpers.js